/**
 * Thin wrapper around the orval-generated model request-log hook.
 *
 * Mirrors useTraces.ts: unwraps the orval v8 `{data, status, headers}` shape
 * into a flat result. The request log is the gateway's always-on D1 audit log
 * scoped to a single model (by slug, server-side).
 */

import type { ApiRequestError } from '@/api/client'
import {
  useListModelRequestsApiModelsModelIdRequestsGet,
} from '@/api/generated/models/models'
import type {
  ListModelRequestsApiModelsModelIdRequestsGetParams,
  RequestLogItem,
  RequestLogResponse,
} from '@/api/generated/model'
import type { ListModelRequestsApiModelsModelIdRequestsGetRange } from '@/api/generated/model/listModelRequestsApiModelsModelIdRequestsGetRange'
import type { RequestStatusFilter } from '@/components/models/home/requestLogOptions'

export type RequestRange = ListModelRequestsApiModelsModelIdRequestsGetRange
export type { RequestStatusFilter }
export type { RequestLogItem, RequestLogResponse }

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

interface UseModelRequestsArgs {
  modelId: string | undefined
  /** Search, filters, sort and paging as the endpoint takes them (TRA-1397). */
  params: ListModelRequestsApiModelsModelIdRequestsGetParams
  /** Off while the toggle shows conversations — the shared log component
      must not fire a request-log query whose table is not rendered. */
  enabled?: boolean
}

export function useModelRequests({
  modelId,
  params,
  enabled = true,
}: UseModelRequestsArgs): UnwrappedQuery<RequestLogResponse> {
  const query = useListModelRequestsApiModelsModelIdRequestsGet(
    modelId ?? '',
    params,
    { query: { enabled: !!modelId && enabled } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}
